import IntranetLayout from "@/components/IntranetLayout";
import { useState } from "react";
import { TrendingUp, TrendingDown, RefreshCw, Download, DollarSign, Globe } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";

interface RegionData {
  name: string; flag: string; subRegion: string; sentiment: "TIGHT" | "BALANCED" | "SOFT";
  fpsoCount: number; rateRange: [number, number]; midPoint: number; voffTarget: number;
  standbyRange: [number, number]; voffStandby: number; trend: string; trendPct: number;
  intel: string[]; competitors: { name: string; vessels: string; rate: string; notes: string }[];
}

const REGIONS: RegionData[] = [
  {
    name: "GUYANA", flag: "🇬🇾", subRegion: "STABROEK BLOCK", sentiment: "TIGHT", fpsoCount: 4,
    rateRange: [16500, 22000], midPoint: 19250, voffTarget: 18500,
    standbyRange: [70, 85], voffStandby: 75, trend: "↑", trendPct: 8,
    intel: [
      "ExxonMobil Guyana added 4th FPSO (Hammerhead) — CTV demand expected to increase Q3 2026",
      "Bourbon Offshore recently awarded Guyana CTV contract — competitor intel",
      "GSBI shore base capacity expanded — logistics costs reducing",
      "Guyana local content requirements increasing — advantage for local-linked operators",
    ],
    competitors: [
      { name: "Bourbon Offshore", vessels: "~3 CTVs", rate: "$17,500–$20,000/d", notes: "Long-term ExxonMobil relationship" },
      { name: "SEACOR Marine", vessels: "~2 CTVs", rate: "$18,000–$21,000/d", notes: "US-flag advantage" },
      { name: "DOF Subsea", vessels: "~1 CTV", rate: "$19,000–$22,000/d", notes: "Premium positioning" },
    ],
  },
  {
    name: "ANGOLA", flag: "🇦🇴", subRegion: "BLOCK 15", sentiment: "BALANCED", fpsoCount: 3,
    rateRange: [14000, 19000], midPoint: 16500, voffTarget: 16000,
    standbyRange: [65, 80], voffStandby: 70, trend: "↑", trendPct: 5,
    intel: [
      "Azule Energy increasing FPSO crew transfer frequency — additional CTV demand flagged",
      "ANPG regulatory changes favouring local partnerships — ALS advantage for VOFF",
      "TotalEnergies Angola extending Block 32 operations — potential CTV expansion",
      "Port of Luanda congestion easing — mobilisation times reducing",
    ],
    competitors: [
      { name: "Tidewater", vessels: "~4 OSVs/CTVs", rate: "$14,000–$18,000/d", notes: "Legacy Angolan presence" },
      { name: "Maersk Supply", vessels: "~2 CTVs", rate: "$15,000–$19,000/d", notes: "Strong Azule relationship" },
      { name: "DOF", vessels: "~1 CTV", rate: "$16,000–$19,500/d", notes: "Spot market focus" },
    ],
  },
  {
    name: "NIGERIA", flag: "🇳🇬", subRegion: "ERHA / USAN", sentiment: "SOFT", fpsoCount: 2,
    rateRange: [12000, 17000], midPoint: 14500, voffTarget: 14000,
    standbyRange: [60, 75], voffStandby: 65, trend: "→", trendPct: -2,
    intel: [
      "ExxonMobil Nigeria maintaining Erha FPSO operations — stable CTV demand",
      "NIMASA cabotage enforcement tightening — foreign-flag vessels facing scrutiny",
      "Security costs increasing in Niger Delta — impacting overall charter economics",
      "Local operators expanding CTV fleet — downward pressure on rates",
    ],
    competitors: [
      { name: "SEACOR Marine", vessels: "~2 CTVs", rate: "$13,000–$16,000/d", notes: "Established ExxonMobil contractor" },
      { name: "Bourbon", vessels: "~3 CTVs", rate: "$12,500–$15,500/d", notes: "Broad West Africa presence" },
      { name: "Miclyn Express", vessels: "~2 CTVs", rate: "$11,000–$14,000/d", notes: "Price-competitive" },
    ],
  },
];

const sentimentBadge = (s: "TIGHT" | "BALANCED" | "SOFT") => {
  const m = { TIGHT: "bg-red-500/15 text-red-400 border-red-500/30", BALANCED: "bg-amber-500/15 text-amber-400 border-amber-500/30", SOFT: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" };
  return <Badge variant="outline" className={`text-[10px] font-bold ${m[s]}`}>{s}</Badge>;
};

const RateIntelligence = () => {
  const [targets, setTargets] = useState<Record<string, number>>(Object.fromEntries(REGIONS.map(r => [r.name, r.voffTarget])));
  const [loading, setLoading] = useState(false);
  const [scenarioRegion, setScenarioRegion] = useState("GUYANA");
  const [scenarioDuration, setScenarioDuration] = useState(12);
  const [scenarioVessels, setScenarioVessels] = useState(1);

  const handleRefresh = async (region: string) => {
    setLoading(true);
    toast.info(`Refreshing ${region} market intelligence...`);
    setTimeout(() => { setLoading(false); toast.success(`${region} intelligence updated`); }, 2000);
  };

  const selectedRegion = REGIONS.find(r => r.name === scenarioRegion)!;
  const target = targets[scenarioRegion] || selectedRegion.voffTarget;
  const standbyRate = Math.round(target * (selectedRegion.voffStandby / 100));
  const annualOp = target * 365 * scenarioVessels;
  const annualStandby = standbyRate * 365 * scenarioVessels;
  const blended = Math.round(annualOp * 0.9 + annualStandby * 0.1);

  return (
    <IntranetLayout>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#14508C] to-[#00283C] rounded-xl p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-[#288CC8] text-xl font-bold tracking-wider uppercase font-display">Rate Intelligence</h1>
            <p className="text-white/60 text-sm font-body mt-1">CTV Market Benchmarking — Guyana | Angola | Nigeria</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-white/40 text-xs font-body">Last Updated: {new Date().toLocaleDateString("en-GB")}</span>
            <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 font-body text-xs" onClick={() => REGIONS.forEach(r => handleRefresh(r.name))}>
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh All
            </Button>
          </div>
        </div>
      </div>

      {/* Three Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {REGIONS.map(region => {
          const t = targets[region.name] || region.voffTarget;
          const rangeDiff = region.rateRange[1] - region.rateRange[0];
          const positionPct = ((t - region.rateRange[0]) / rangeDiff) * 100;
          const position = positionPct < 33 ? "LOWER" : positionPct < 66 ? "MID" : "UPPER";
          return (
            <div key={region.name} className="bg-card border border-border rounded-xl overflow-hidden">
              {/* Region Header */}
              <div className="bg-[#00283C] p-4 flex items-center justify-between">
                <div>
                  <h3 className="text-white text-sm font-bold font-display">{region.flag} {region.name} — {region.subRegion}</h3>
                  <p className="text-white/40 text-[10px] font-body mt-0.5">{region.fpsoCount} Active FPSOs</p>
                </div>
                {sentimentBadge(region.sentiment)}
              </div>

              {/* Rate Card */}
              <div className="p-4 space-y-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-body mb-1">CTV Day Rates</p>
                  <div className="space-y-1 text-xs font-body">
                    <div className="flex justify-between"><span className="text-muted-foreground">Market Range:</span><span className="text-foreground font-medium">${region.rateRange[0].toLocaleString()} – ${region.rateRange[1].toLocaleString()}/d</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Mid-Point:</span><span className="text-foreground">${region.midPoint.toLocaleString()}/d</span></div>
                  </div>
                  {/* Rate bar */}
                  <div className="relative mt-3 mb-1">
                    <div className="h-2 bg-muted rounded-full">
                      <div className="absolute h-2 rounded-full bg-gradient-to-r from-[#288CC8]/40 to-[#288CC8]" style={{ width: "100%" }} />
                      <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#8CB43C] border-2 border-white shadow" style={{ left: `${positionPct}%` }} />
                    </div>
                    <div className="flex justify-between text-[9px] text-muted-foreground font-body mt-1">
                      <span>${region.rateRange[0].toLocaleString()}</span>
                      <span>${region.rateRange[1].toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-muted-foreground font-body">VOFF Target:</span>
                    <Input
                      type="number" value={t}
                      onChange={e => setTargets(prev => ({ ...prev, [region.name]: parseInt(e.target.value) || 0 }))}
                      className="w-28 h-7 text-xs font-body font-bold"
                    />
                    <Badge variant="outline" className="text-[9px] font-bold text-[#8CB43C] border-[#8CB43C]/30">{position}</Badge>
                  </div>
                </div>

                <div className="text-xs font-body space-y-1">
                  <div className="flex justify-between"><span className="text-muted-foreground">Standby ({region.voffStandby}%):</span><span className="text-foreground">${Math.round(t * region.voffStandby / 100).toLocaleString()}/d</span></div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Market Trend:</span>
                    <span className={`font-medium ${region.trendPct > 0 ? "text-emerald-400" : region.trendPct < 0 ? "text-red-400" : "text-amber-400"}`}>
                      {region.trend} {region.trendPct > 0 ? "+" : ""}{region.trendPct}% vs Q1 2026
                    </span>
                  </div>
                </div>

                {/* Intel Feed */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-body font-bold">Intelligence</p>
                    <Button size="sm" variant="ghost" className="h-6 text-[10px] font-body" onClick={() => handleRefresh(region.name)}>
                      <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {region.intel.map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="text-[#288CC8] mt-1 shrink-0">•</span>
                        <span className="text-[11px] text-foreground/80 font-body">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Competitors */}
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-body font-bold mb-2">Competitors</p>
                  <div className="space-y-2">
                    {region.competitors.map((c, i) => (
                      <div key={i} className="bg-muted/30 rounded-lg p-2.5 border border-border">
                        <p className="text-xs font-bold text-foreground font-body">{c.name}</p>
                        <p className="text-[10px] text-muted-foreground font-body">{c.vessels} · {c.rate}</p>
                        <p className="text-[10px] text-muted-foreground/70 font-body italic">{c.notes}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom: Rate Positioning Tool */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-sm font-bold text-foreground font-display mb-4">Rate Positioning Tool</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground font-body mb-1 block">Region</label>
                <Select value={scenarioRegion} onValueChange={setScenarioRegion}>
                  <SelectTrigger className="font-body text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{REGIONS.map(r => <SelectItem key={r.name} value={r.name}>{r.flag} {r.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-body mb-1 block">Duration (months)</label>
                <Input type="number" value={scenarioDuration} onChange={e => setScenarioDuration(parseInt(e.target.value) || 1)} className="font-body text-sm" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-body mb-1 block">Vessel Type</label>
                <Select defaultValue="crewliner">
                  <SelectTrigger className="font-body text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="crewliner">Crewliner</SelectItem><SelectItem value="combi">Combi</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-body mb-1 block">Number of Vessels</label>
                <Input type="number" value={scenarioVessels} onChange={e => setScenarioVessels(parseInt(e.target.value) || 1)} className="font-body text-sm" />
              </div>
            </div>
          </div>
          <div className="bg-[#00283C] rounded-xl p-5 text-white font-body">
            <p className="text-[10px] uppercase tracking-wider text-white/40 mb-3 font-bold">Recommended Rate Positioning</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-white/60">Operating Day Rate:</span><span className="font-bold">${target.toLocaleString()}/day</span></div>
              <div className="flex justify-between"><span className="text-white/60">Standby Rate ({selectedRegion.voffStandby}%):</span><span>${standbyRate.toLocaleString()}/day</span></div>
              <div className="flex justify-between"><span className="text-white/60">Mob / Demob:</span><span>$45,000 (flat)</span></div>
              <div className="border-t border-white/10 my-3" />
              <div className="flex justify-between"><span className="text-white/60">{scenarioDuration}-month Contract Value (est.):</span></div>
              <div className="flex justify-between text-xs"><span className="text-white/40 ml-2">Operating scenario:</span><span>${(annualOp * scenarioDuration / 12).toLocaleString()}</span></div>
              <div className="flex justify-between text-xs"><span className="text-white/40 ml-2">Standby scenario:</span><span>${(annualStandby * scenarioDuration / 12).toLocaleString()}</span></div>
              <div className="flex justify-between text-xs"><span className="text-white/40 ml-2">Blended (90% util.):</span><span className="text-[#8CB43C] font-bold">${(blended * scenarioDuration / 12).toLocaleString()}</span></div>
              <div className="border-t border-white/10 my-3" />
              <div className="flex justify-between text-xs"><span className="text-white/40">Negotiation Floor:</span><span className="text-red-400 font-bold">${(selectedRegion.rateRange[0]).toLocaleString()}/day</span></div>
            </div>
            <Button size="sm" variant="outline" className="mt-4 border-white/20 text-white hover:bg-white/10 font-body text-xs w-full" onClick={() => toast.success("Rate card exported as PDF")}>
              <Download className="w-3.5 h-3.5 mr-1" /> Export Rate Card
            </Button>
          </div>
        </div>
      </div>
    </IntranetLayout>
  );
};

export default RateIntelligence;
